/**
 * AVA-475 — Cloud pick.
 *
 * Arms a one-shot click handler that resolves a click on the point cloud to a
 * WORLD-space {x,y,z} in VIEWER units, so the surveyor can capture an as-built
 * point (ticket Step 3). Positions stay exactly as the viewer stated them —
 * metres on some facilities, millimetres on others (utils/modelUnits.js) — and
 * `pointMath.computeDeltas` applies the model's scale when it turns two
 * positions into a millimetre deviation. This file converts nothing.
 *
 * WHY MainExtension AND NOT A RAYCASTER
 * The first version of this file walked the scene graph with
 * `intersectRecursively`, which matches on `object instanceof THREE.PointCloud`
 * and iterates `children`. Probing a real loaded cloud in the browser showed
 * that CANNOT work:
 *   - the object is a `PointCloudOctree` (proto: PointCloudOctree -> PointCloudTree)
 *   - `instanceof THREE.PointCloud` -> false
 *   - `children.length` -> 0   (an octree streams points by level-of-detail,
 *     so its points are never THREE children)
 * Every pick would have silently returned "missed the cloud".
 *
 * `MainExtension` already exposes octree-aware picking, which is what the rest
 * of the codebase uses. We call that instead of re-deriving the maths.
 *
 * MIND THE SIGNATURE — it takes TWO NUMBERS, not an event:
 *     getMouseIntersectionPointCloud(clientX, clientY) -> {x, y, z} | null
 * Passing an event (or a `{clientX, clientY}` object) returns `null` — verified
 * live, side by side, at the same pixel. So does
 * `pickNearestPointCloudPoint({clientX, clientY})`, which is why it is not in
 * the chain below. Both fail SILENTLY, which is exactly how the first two
 * versions of this file looked correct while never picking anything.
 *
 * `Potree.Utils.getMousePointCloudIntersection(mouse, camera, viewer,
 * pointclouds, { pickClipped: true })` is the documented fallback — see
 * `features/PotreeMeasurements/components/PotreeViewer.vue:389`.
 * `pickClipped` matters: the as-built pick happens INSIDE a slice, so the
 * intersection must consider clipped geometry.
 *
 * Returned coordinates are WORLD-space VIEWER units, quantised by the cloud
 * itself — the API hands back a real stored point, so accuracy is floored by
 * point spacing (~1 mm on the scans measured).
 *
 * ACCURACY IS A FUNCTION OF ZOOM. Measured with the same +/-3 px wobble:
 *   whole-building view (13.9 mm/px) -> 145 mm scatter   (useless vs +/-3 mm)
 *   300 mm framed view  (0.4 mm/px)  ->   4 mm scatter   (inside tolerance)
 * A pick taken from an un-zoomed view is not a valid measurement. Callers must
 * frame the view first and should gate on `isZoomAdequate` (below), fed by
 * SliceTabs.measureMmPerPx.
 *
 * Only one pick can be armed at a time; arming again replaces the previous one.
 */

import store from '@/store';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

/** Active listener bookkeeping so we can always detach cleanly. */
let activeHandler = null;
let activeDownHandler = null;
let activeTarget = null;
let downAt = null;

/**
 * How far (px) the pointer may travel between press and release and still count
 * as a pick rather than a camera drag. Mirrors the model-pick guard in
 * VerificationViewer.vue.
 */
const DRAG_THRESHOLD_PX = 4;

/**
 * A pixel must be at most tolerance/N to assess that tolerance. N = 3 keeps aim
 * error well inside the pass/fail boundary — see `isZoomAdequate`.
 */
const TOLERANCE_PIXEL_RATIO = 3;

function getMainExtension() {
  try {
    return store.state.forgeService?.getMainExtension?.() ?? null;
  } catch (err) {
    debugError('[cloudPick] could not reach MainExtension', err);
    return null;
  }
}

/** Loaded point clouds, as the extension tracks them. */
export function getPointCloudObjects() {
  const mainExt = getMainExtension();
  if (!mainExt || !mainExt._pointclouds) return [];
  return Array.from(mainExt._pointclouds.values()).filter(Boolean);
}

/** Normalise the various shapes the pick APIs return to a plain point. */
function toPoint(result) {
  const p = result?.location || result?.point || result?.position || result;
  if (!p || typeof p.x !== 'number') return null;
  return { x: p.x, y: p.y, z: p.z };
}

/**
 * Resolve a pointer event to a world-space point (VIEWER units) on the cloud.
 * @returns {{x:number,y:number,z:number}|null}
 */
export function pickCloudPointAt(viewer, pointerEvent) {
  const mainExt = getMainExtension();
  if (!mainExt) return null;

  if (!getPointCloudObjects().length) {
    debugWarn('[cloudPick] no point clouds loaded');
    return null;
  }

  // Preferred: the extension's own octree-aware intersection.
  // TWO NUMBERS — passing the event returns null (see file header).
  if (typeof mainExt.getMouseIntersectionPointCloud === 'function') {
    try {
      const point = toPoint(
        mainExt.getMouseIntersectionPointCloud(pointerEvent.clientX, pointerEvent.clientY),
      );
      if (point) return point;
    } catch (err) {
      debugError('[cloudPick] getMouseIntersectionPointCloud failed', err);
    }
  }

  // Fallback: Potree's own intersection, as used by PotreeViewer.vue.
  try {
    const Potree = window.Potree || window.ForgePotree;
    const getIntersection = Potree?.Utils?.getMousePointCloudIntersection;
    const camera = viewer?.impl?.camera;
    const canvas = viewer?.canvas;
    if (typeof getIntersection === 'function' && camera && canvas) {
      const rect = canvas.getBoundingClientRect();
      const mouse = {
        x: pointerEvent.clientX - rect.left,
        y: pointerEvent.clientY - rect.top,
      };
      const point = toPoint(
        getIntersection(mouse, camera, viewer, getPointCloudObjects(), { pickClipped: true }),
      );
      if (point) return point;
    }
  } catch (err) {
    debugError('[cloudPick] Potree intersection fallback failed', err);
  }

  debugWarn('[cloudPick] click did not hit the cloud');
  return null;
}

/*
 * REMOVED: measureScreenScaleMm.
 *
 * It measured world-mm-per-pixel by CLOUD-PICKING at two points a span apart,
 * which made the measurement depend on both probes landing on geometry. Near an
 * edge, or over a gap in the cloud, one probe misses and the whole reading comes
 * back null — precisely where the scale matters most. Worse, if a probe landed
 * on the far surface through a gap, the span it reported was the distance to
 * something metres behind, overstating mm/px and crying wolf on an adequate view.
 *
 * SliceTabs.measureMmPerPx replaces it: it unprojects two screen points through
 * the camera and needs no geometry at all, so it cannot fail for either reason.
 * `isZoomAdequate` below is unchanged and still takes a `{ horiz, vert }` pair.
 */

/**
 * Is the view zoomed in far enough to assess `toleranceMm`?
 *
 * A pixel must be small relative to the tolerance or aim error swamps the
 * measurement. One third is the working rule: at +/-3 mm it demands <= 1 mm/px,
 * which the 300 mm framed view (0.4 mm/px) clears and the building overview
 * (13.9 mm/px) fails by ~14x.
 */
export function isZoomAdequate(scaleMm, toleranceMm) {
  if (!scaleMm || !toleranceMm) return false;
  const worst = Math.max(scaleMm.horiz ?? Infinity, scaleMm.vert ?? Infinity);
  return worst <= toleranceMm / TOLERANCE_PIXEL_RATIO;
}

/**
 * Arm a one-shot cloud pick.
 * @param {object} viewer
 * @param {(point:{x,y,z}) => void} onPick called once, with a world point in
 *   VIEWER units
 * @returns {() => void} disposer — always safe to call
 */
export function enableCloudPick(viewer, onPick) {
  if (!viewer || typeof onPick !== 'function') return () => {};

  // Replace any previously armed pick so handlers can never stack up.
  disableCloudPick();

  const target = viewer.canvas || viewer.container;
  if (!target) {
    debugError('[cloudPick] no canvas to attach to');
    return () => {};
  }

  activeDownHandler = (event) => {
    downAt = { x: event.clientX, y: event.clientY };
  };

  activeHandler = (event) => {
    // A camera drag also ends in a click — ignore it and stay armed.
    if (downAt) {
      const dx = event.clientX - downAt.x;
      const dy = event.clientY - downAt.y;
      if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD_PX) {
        debugLog('[cloudPick] drag ignored — pick still armed');
        return;
      }
    }
    const point = pickCloudPointAt(viewer, event);
    if (!point) return; // a miss leaves the pick armed — the surveyor just retries
    debugLog('[cloudPick] picked (viewer units)', point);
    disableCloudPick();
    onPick(point);
  };

  target.addEventListener('mousedown', activeDownHandler);
  target.addEventListener('click', activeHandler);
  activeTarget = target;
  debugLog('[cloudPick] armed');

  return () => disableCloudPick();
}

/** Detach the armed pick, if any. Idempotent. */
export function disableCloudPick() {
  if (activeTarget) {
    if (activeHandler) activeTarget.removeEventListener('click', activeHandler);
    if (activeDownHandler) activeTarget.removeEventListener('mousedown', activeDownHandler);
    debugLog('[cloudPick] disarmed');
  }
  activeHandler = null;
  activeDownHandler = null;
  activeTarget = null;
  downAt = null;
}
